/**
 * App.tsx - Mobile First PWA Version
 * 
 * This version is optimized for mobile devices with PWA support and offline functionality.
 * It follows a mobile-first responsive design approach while maintaining a clean architecture
 * and optimized loading sequence for an excellent user experience across all devices.
 */

import React, { Suspense, lazy, useState, useEffect } from "react";
import { Routes, Route } from "react-router-dom";

// Legal pages - using relative paths for better compatibility
const PrivacyPolicy = lazy(() => import('./components/legal/PrivacyPolicy').then(module => ({ default: module.PrivacyPolicy })));
const Terms = lazy(() => import('./components/legal/Terms').then(module => ({ default: module.Terms })));
const Disclaimer = lazy(() => import('./components/legal/Disclaimer').then(module => ({ default: module.Disclaimer })));
const GetTheApp = lazy(() => import('./pages/GetTheApp'));
import { HelmetProvider } from 'react-helmet-async';
import { Toaster } from "@/components/ui/sonner";
import { TooltipProvider } from "@/components/ui/tooltip";
import { ErrorBoundary } from "@/components/ErrorBoundary";
import { OptimizedHeader } from "@/components/layout/OptimizedHeader";
import { Footer } from "@/components/layout/Footer";
import { OptimizedThemeProvider } from "@/contexts/OptimizedThemeContext";
import { PullToRefresh } from "@/components/ui/pull-to-refresh";
import { OfflineProvider } from "@/contexts/OfflineContext";
import { OfflineIndicator } from "@/components/common/OfflineIndicator";
import { OfflineRouter } from "@/components/common/OfflineRouter";
import PrivacyConsentBanner from "@/components/common/PrivacyConsentBanner";
import InsightNotification from "@/components/insights/InsightNotification";
import { posthogService } from "@/services/PostHogService";
// Service worker is automatically registered by Vite PWA plugin
import { insightService } from "@/services/InsightService";
import logger from "@/utils/logger";
import "@/styles/notifications.css";

// Import diagnostic component for accessibility debugging
import { DialogAccessibilityDebug } from "@/components/dev/DialogAccessibilityDebug";

import Index from "@/pages/Index";
import PricingPage from "@/pages/Pricing";
import Signup from "@/pages/Signup";
import Debugger from "@/pages/Debugger";
import Features from "@/pages/Features";
import HowItWorks from "@/pages/HowItWorks";
import Caregivers from "@/pages/Caregivers";
import FAQ from "@/pages/FAQ";
import InsightsPage from "@/pages/InsightsPage";
import TestHookExecution from "@/pages/TestHookExecution";
import HookTestingDashboard from "@/pages/HookTestingDashboard";
import IndexComponentTest from "@/pages/IndexComponentTest";
import HookTestingHub from "@/pages/HookTestingHub";
import EmailTestPage from "@/pages/email-test";
import DialogAccessibilityTest from "@/components/tests/DialogAccessibilityTest";

/**
 * UI Components
 */

// Mobile-optimized loading spinner with improved UX
const LoadingSpinner = () => (
  <div className="flex flex-col items-center justify-center min-h-screen px-4">
    <div className="animate-spin rounded-full h-12 w-12 border-t-2 border-b-2 border-primary mb-4"></div>
    <p className="text-muted-foreground text-center">Loading Meduler...</p>
    <p className="text-xs text-muted-foreground/70 mt-2 text-center">Optimized for mobile and offline use</p>
  </div>
);

// Mobile-friendly error fallback component with retry option
const ErrorFallback = ({ error, resetErrorBoundary }: { error: Error, resetErrorBoundary: () => void }) => (
  <div className="p-4 md:p-8 text-center max-w-md mx-auto mt-10 md:mt-20">
    <h2 className="text-xl md:text-2xl font-bold mb-3 md:mb-4">Something went wrong</h2>
    <p className="mb-3 md:mb-4 text-muted-foreground text-sm md:text-base">We encountered an error while loading this page.</p>
    <p className="mb-3 text-xs text-muted-foreground/70">If you're offline, try again when you have a connection</p>
    {error && process.env.NODE_ENV === 'development' && (
      <pre className="bg-gray-100 p-3 md:p-4 rounded mb-4 text-left overflow-auto text-xs md:text-sm">
        {error.message}
      </pre>
    )}
    <button 
      onClick={resetErrorBoundary}
      className="px-4 py-2 bg-primary text-white rounded-md text-sm md:text-base"
    >
      Try Again
    </button>
  </div>
);

// Import About page 
import AboutUs from "./pages/AboutUs";
import Contact from "./pages/Contact";

// Import Offline page
import OfflinePage from "./pages/Offline";
import NotFound from "./pages/NotFound";

// Custom error UI component - used outside ErrorBoundary when we catch errors manually
const ErrorDisplay = ({ error, onRetry }: { error: Error, onRetry: () => void }) => (
  <div className="p-8 text-center max-w-md mx-auto mt-20">
    <h2 className="text-2xl font-bold mb-4">Something went wrong</h2>
    <p className="mb-4 text-muted-foreground">We encountered an error while loading this page.</p>
    {error && process.env.NODE_ENV === 'development' && (
      <pre className="bg-gray-100 p-4 rounded mb-4 text-left overflow-auto text-sm">
        {error.message}
      </pre>
    )}
    <button 
      onClick={onRetry}
      className="px-4 py-2 bg-primary text-white rounded-md"
    >
      Try Again
    </button>
  </div>
);





/**
 * App component with improved error handling and minimal initialization
 */
function App() {
  const [initialized, setInitialized] = useState(false);

  // Force profile to be U1 on app startup (will only execute once)
  useEffect(() => {
    // Always default to U1 unless explicitly set to U2
    const currentProfileId = localStorage.getItem('currentProfileId');
    if (!currentProfileId || (currentProfileId !== 'u1' && currentProfileId !== 'u2')) {
      localStorage.setItem('currentProfileId', 'u1');
      logger.info('[App] Forced default profile to U1');
    }
  }, []);

  // Mobile-optimized initialization effect - very lightweight with no dependencies that could hang
  useEffect(() => {
    // Set a timeout to prevent hanging indefinitely on mobile devices with poor connections
    const timeout = setTimeout(() => {
      if (!initialized) {
        logger.info('Initialization timed out, forcing app to render');
        setInitialized(true);
      }
    }, 3000);


    // Setup any minimal required services here
    const initApp = async () => {
      try {
        logger.info('Initializing app with mobile-first dependencies...');
        // Check for offline capability
        const isOnline = navigator.onLine;
        if (!isOnline) {
          logger.info('Offline mode detected - using cached data');
        }

        // Ensure default profile is set to U1 in localStorage if not already set
        if (!localStorage.getItem('currentProfileId')) {
          localStorage.setItem('currentProfileId', 'u1');
          logger.info('Default profile initialized to U1');
        }

        // Initialize analytics with enhanced PUID system
        await posthogService.initialize({
          apiKey: import.meta.env.VITE_POSTHOG_API_KEY || '',
          apiHost: import.meta.env.VITE_POSTHOG_HOST || 'https://app.posthog.com',
          capturePageview: false, // We'll do this manually to ensure consent
          autocapture: false,      // We don't want to auto-capture for privacy reasons
          debug: import.meta.env.MODE === 'development',
        });

        // Mark as initialized
        setInitialized(true);
      } catch (error) {
        logger.error('Error during initialization:', error);
        // Still mark as initialized to prevent hanging
        setInitialized(true);
      }
    };

    initApp();

    return () => clearTimeout(timeout);
  }, [initialized]);

  // Show loading spinner while initializing
  if (!initialized) {
    return <LoadingSpinner />;
  }

  return (
    <div className="app-root">
      <ErrorBoundary>
        <OptimizedThemeProvider>
          <OfflineProvider>
            <HelmetProvider>
              <TooltipProvider>
                <OfflineRouter>
                  <PullToRefresh>
                {/* Include the optimized header */}
                <OptimizedHeader />

                {/* Mobile-friendly main content wrapper with proper padding for the taller header */}
                <div className="pb-16 pt-2 md:pt-4 min-h-[calc(100vh-5rem-4rem)]">
                  {/* Main content with padding for all pages */}
                  <Toaster position="top-right" className="z-50" closeButton richColors />
                  <OfflineIndicator />
                  <PrivacyConsentBanner onConsentChange={(hasConsent) => {
                    if (hasConsent) {
                      posthogService.trackPageView(window.location.pathname);
                    }
                  }} />
                  {/* Add diagnostic component for development only */}
                  {process.env.NODE_ENV === 'development' && <DialogAccessibilityDebug />}
                  <Routes>
                    {/* Main route - Index as the homepage */}
                    <Route path="/" element={<Index />} />

                    {/* About page route */}
                    <Route path="/about" element={<AboutUs />} />
                    <Route path="/contact" element={<Contact />} />

                    {/* Offline page route */}
                    <Route path="/offline" element={<OfflinePage />} />

                    {/* Pricing page route */}
                    <Route path="/pricing" element={<PricingPage />} />

                    {/* Signup page route - redirects to pricing with waitlist form */}
                    <Route path="/signup" element={<Signup />} />

                    {/* New dedicated landing pages */}
                    <Route path="/features" element={<Features />} />
                    <Route path="/how-it-works" element={<HowItWorks />} />
                    <Route path="/caregivers" element={<Caregivers />} />
                    <Route path="/faq" element={<FAQ />} />

                    {/* Hidden debugger view for development purposes */}
                    <Route path="/debugger" element={<Debugger />} />

                    {/* Marketing landing page for App Store */}
                    <Route path="/get-the-app" element={
                      <Suspense fallback={<div className="p-8 flex justify-center items-center"><div className="animate-spin h-8 w-8 border-4 border-purple-500 rounded-full border-t-transparent"></div></div>}>
                        <GetTheApp />
                      </Suspense>
                    } />

                    {/* Legal pages */}
                    <Route path="/privacy" element={
                      <Suspense fallback={<div className="p-8 flex justify-center items-center"><div className="animate-spin h-8 w-8 border-4 border-purple-500 rounded-full border-t-transparent"></div></div>}>
                        <PrivacyPolicy />
                      </Suspense>
                    } />
                    <Route path="/terms" element={
                      <Suspense fallback={<div className="p-8 flex justify-center items-center"><div className="animate-spin h-8 w-8 border-4 border-purple-500 rounded-full border-t-transparent"></div></div>}>
                        <Terms />
                      </Suspense>
                    } />
                    <Route path="/disclaimer" element={
                      <Suspense fallback={<div className="p-8 flex justify-center items-center"><div className="animate-spin h-8 w-8 border-4 border-purple-500 rounded-full border-t-transparent"></div></div>}>
                        <Disclaimer />
                      </Suspense>
                    } />

                    {/* Test pages for hook execution validation */}
                    <Route path="/hook-testing-hub" element={<HookTestingHub />} />
                    <Route path="/test-hooks" element={<TestHookExecution />} />
                    <Route path="/hook-testing" element={<HookTestingDashboard />} />
                    <Route path="/index-test" element={<IndexComponentTest />} />
                    
                    {/* Accessibility testing */}
                    <Route path="/a11y-dialog-test" element={<DialogAccessibilityTest />} />
                    
                    {/* Email testing page */}
                    <Route path="/email-test" element={<EmailTestPage />} />

                    {/* Original landing page (still accessible) */}
                    <Route path="/landing" element={
                      <div className="min-h-[calc(100vh-8rem)] flex items-center justify-center p-4">
                        <div className="bg-white dark:bg-gray-800 rounded-lg shadow-lg p-4 md:p-8 max-w-md w-full">
                          <h1 className="text-xl md:text-2xl font-bold mb-3 md:mb-4">Meduler</h1>
                          <p className="mb-2 text-sm md:text-base">Welcome to Meduler</p>
                          <p className="text-gray-600 dark:text-gray-300 text-sm md:text-base">Premium tier: Meduler Plus</p>
                          <div className="mt-4 md:mt-6">
                            <a 
                              href="/about"
                              className="px-3 md:px-4 py-1.5 md:py-2 bg-primary text-white rounded-md inline-block hover:bg-primary/90 transition-colors text-sm md:text-base"
                            >
                              Learn More
                            </a>
                          </div>
                        </div>
                      </div>
                    } />

                    {/* Unknown client-side paths use the same recovery experience as the server. */}
                    <Route path="*" element={<NotFound />} />
                  </Routes>
                </div>

                {/* Global footer for consistent user experience */}
                <Footer />
                </PullToRefresh>
                </OfflineRouter>
              </TooltipProvider>
            </HelmetProvider>
          </OfflineProvider>
        </OptimizedThemeProvider>
      </ErrorBoundary>

    </div>
  );
}
// Configure default fetch headers (defined outside of component for global effect)
if (typeof window !== 'undefined') {
  const originalFetch = window.fetch;
  window.fetch = function(input: RequestInfo | URL, init?: RequestInit) {
    if (!init) init = {};
    if (!init.headers) init.headers = {};
    Object.assign(init.headers, {
      'Access-Control-Allow-Origin': '*',
      'Access-Control-Allow-Methods': 'GET, POST, PUT, DELETE, OPTIONS',
      'Access-Control-Allow-Headers': 'Content-Type, Authorization'
    });
    return originalFetch(input, init);
  };
}

export default App;